import React, { useState } from 'react';
import { IconButton, CircularProgress } from '@mui/material';
import { Email, Lock, Person, Phone, Visibility, VisibilityOff } from '@mui/icons-material';

import PrimaryButton from '../../common/buttons/PrimaryButton';
import InputField from '../../common/fields/InputField';
import AppSnackbar from '../../common/popups/AppSnackbar';
import useAuth from '../../../hooks/useAuth';
import { validateRegister } from '../../../validations/registerValidation';

export default function Register({ onEmailSent }) {
  const { register } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) {
      const { [field]: _, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateRegister(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await register(formData);
      onEmailSent(formData.email);
    } catch (error) {
      const data = error.response?.data;

      if (error.response?.status === 422 && data?.errors) {
        const mappedErrors = {};
        Object.keys(data.errors).forEach((key) => {
          mappedErrors[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key];
        });
        setErrors(mappedErrors);
      } else {
        setErrors({ submit: data?.message ?? 'An unexpected error occurred' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <h2 className="text-3xl font-bold text-white mb-2 text-center">Register as Teacher</h2>
      <p className="text-gray-400 text-center mb-6">Join our teaching community</p>

      {errors.submit && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
          <p className="text-red-400 text-sm text-center">{errors.submit}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="First Name"
            type="text"
            value={formData.first_name}
            onChange={handleChange('first_name')}
            error={!!errors.first_name}
            helperText={errors.first_name}
            disabled={loading}
            slotProps={{
              input: {
                startAdornment: <Person sx={{ color: '#F6911D', mr: 1 }} />,
              },
            }}
          />
          <InputField
            label="Last Name"
            type="text"
            value={formData.last_name}
            onChange={handleChange('last_name')}
            error={!!errors.last_name}
            helperText={errors.last_name}
            disabled={loading}
            slotProps={{
              input: {
                startAdornment: <Person sx={{ color: '#F6911D', mr: 1 }} />,
              },
            }}
          />
        </div>

        {/* Email */}
        <InputField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          disabled={loading}
          slotProps={{
            input: {
              startAdornment: <Email sx={{ color: '#F6911D', mr: 1 }} />,
            },
          }}
        />

        {/* Phone */}
        <InputField
          label="Phone Number"
          type="tel"
          value={formData.phone}
          onChange={handleChange('phone')}
          error={!!errors.phone}
          helperText={errors.phone}
          disabled={loading}
          inputProps={{
            inputMode: 'numeric',
            maxLength: 10,
            pattern: '0[0-9]{9}',
          }}
          slotProps={{
            input: {
              startAdornment: <Phone sx={{ color: '#F6911D', mr: 1 }} />,
            },
          }}
        />

        {/* Password */}
        <InputField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          error={!!errors.password}
          helperText={errors.password}
          disabled={loading}
          slotProps={{
            input: {
              startAdornment: <Lock sx={{ color: '#F6911D', mr: 1 }} />,
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                  tabIndex={-1}
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            },
          }}
        />

        {/* Confirm Password */}
        <InputField
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.password_confirmation}
          onChange={handleChange('password_confirmation')}
          error={!!errors.password_confirmation}
          helperText={errors.password_confirmation}
          disabled={loading}
          slotProps={{
            input: {
              startAdornment: <Lock sx={{ color: '#F6911D', mr: 1 }} />,
              endAdornment: (
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                  tabIndex={-1}
                  disabled={loading}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            },
          }}
        />

        {/* Submit */}
        <div className="flex justify-center">
          <PrimaryButton type="submit" disabled={loading} fullWidth>
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create Account'}
          </PrimaryButton>
        </div>

        <p className="text-gray-400 text-center text-xs sm:text-sm">
          By registering, you'll complete your profile in the next step
        </p>
      </form>

      {/* Snackbar */}
      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
}
